/**
 * Ad-Hoc Interest Payment Dialog
 * Requests either an ad-hoc interest payment (pays out interest at the chosen
 * date) OR an accrual adjustment (a signed correction to accrued interest that
 * does not pay out but is folded into the contract's NEXT payment). The mode is
 * chosen at the "Interest Amendments" dropdown on the contract action bar and
 * passed in via `initialMode`; both create a pending_changes entry for approval.
 */

import React, { useState, useEffect } from 'react';
import {
  TextField, Grid, InputAdornment, Alert, Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { apiPost, apiGet } from '../../utils/apiClient';
import ContractActionDialogBase from './ContractActionDialogBase';
import ScheduleImpactPreviewModal from './ScheduleImpactPreviewModal';

const AdhocInterestPaymentDialog = ({ open, onClose, contract, contractId, onSuccess, initialMode = 'payment' }) => {
  // mode: 'payment' pays out interest at the date; 'accrual_adjustment' books a
  // signed correction folded into the next payment. Set from the action-bar
  // dropdown via initialMode; there is no in-dialog switch.
  const [mode, setMode] = useState(initialMode);
  const [formData, setFormData] = useState({
    event_date: format(new Date(), 'yyyy-MM-dd'),
    description: '',
    amount: '',
  });
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const isAdjustment = mode === 'accrual_adjustment';
  // Accrual adjustment isn't supported on equal-installment contracts in v1
  // (the payout is a solved constant installment, not accumulated gross). The
  // primary guard is the disabled dropdown item; this is belt-and-braces.
  const eiBlocksAdjustment = !!contract?.equal_installment;
  const isMultiInvestor = !!contract?.is_multi_investor || (Array.isArray(contract?.investors) && contract.investors.length > 1);

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setFormData({
        event_date: format(new Date(), 'yyyy-MM-dd'),
        description: '',
        amount: '',
      });
      setReason('');
      setError(null);
      setPreviewData(null);
      setPreviewModalOpen(false);
    }
  }, [open, initialMode]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (['event_date', 'amount'].includes(field)) {
      setPreviewData(null);
    }
  };

  const handlePreview = async () => {
    if (!formData.event_date) return;
    if (isAdjustment && formData.amount === '') {
      setError('An adjustment amount is required to preview an accrual adjustment');
      return;
    }
    const cid = contractId || contract?.id;
    if (!cid) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ event_date: formData.event_date });
      if (formData.amount !== '') params.append('amount', formData.amount);
      if (isAdjustment) params.append('mode', 'accrual_adjustment');
      const response = await apiGet(`/amendments/contract/${cid}/interest-preview?${params.toString()}`);
      if (response?.success) {
        setPreviewData(response.data);
        setPreviewModalOpen(true);
      } else {
        setError(response?.message || 'Failed to generate preview');
      }
    } catch (err) {
      setError('Failed to generate preview: ' + (err.response?.data?.message || err.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async ({ proof_document_ids }) => {
    if (!formData.event_date) {
      setError('Event date is required');
      return;
    }
    // Belt-and-braces: accrual adjustment is unsupported on equal-installment in
    // v1 (the dropdown item is disabled, but guard the submit path too).
    if (isAdjustment && eiBlocksAdjustment) {
      setError('Accrual adjustments are not available for equal-installment contracts yet.');
      return;
    }
    if (contract?.maturity_date && new Date(formData.event_date) >= new Date(contract.maturity_date)) {
      setError('Event date must be before the contract maturity date');
      return;
    }
    if (contract?.deposit_date && new Date(formData.event_date) <= new Date(contract.deposit_date)) {
      setError('Event date must be after the contract deposit date');
      return;
    }
    let parsedAmount = null;
    if (isAdjustment) {
      if (formData.amount === '') {
        setError('An adjustment amount is required');
        return;
      }
      parsedAmount = parseFloat(formData.amount);
      if (!Number.isFinite(parsedAmount) || parsedAmount === 0) {
        setError('Adjustment amount must be a non-zero number (negative to reduce accrued interest)');
        return;
      }
    } else if (formData.amount !== '') {
      parsedAmount = parseFloat(formData.amount);
      if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
        setError('Interest amount must be a positive number');
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await apiPost(`/contracts/${contractId || contract?.id}/adhoc-interest-payments`, {
        event_date: formData.event_date,
        description: formData.description || null,
        motivation: reason,
        amount: parsedAmount,
        mode,
        proof_document_ids,
      });
      if (response?.success) {
        onClose();
        if (onSuccess) onSuccess();
      } else {
        setError(response?.message || 'Failed to submit interest payment request');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit interest payment request');
    } finally {
      setSubmitting(false);
    }
  };

  // Adjustment needs a non-zero amount to be valid; payment only needs a date.
  const isValid = !!formData.event_date && (!isAdjustment || formData.amount !== '');

  return (
    <>
    <ContractActionDialogBase
      open={open}
      onClose={onClose}
      contract={contract}
      title={isAdjustment ? 'Accrual Adjustment' : 'Ad-Hoc Interest Payment'}
      description={isAdjustment
        ? 'Book a correction to accrued interest (e.g. a picked-up accrual error). It is NOT paid out on the effective date — it is folded into the contract’s next payment. The schedule will be regenerated upon approval.'
        : 'Add an interest payment to the amortization schedule. Specify an amount or leave blank to auto-calculate accrued interest. The schedule will be regenerated upon approval.'}
      submitLabel="Submit for Approval"
      submitColor="info"
      isValid={isValid}
      submitting={submitting}
      error={error}
      onClearError={() => setError(null)}
      onSubmit={handleSubmit}
      onPreview={handlePreview}
      showPreview={true}
      previewLoading={previewLoading}
      reason={reason}
      onReasonChange={setReason}
      reasonLabel="Motivation (Required)"
      reasonPlaceholder={isAdjustment
        ? 'Provide a motivation for this accrual adjustment (e.g. what error is being corrected). This will be reviewed by an approver.'
        : 'Provide a motivation for this ad-hoc interest payment. This will be reviewed by an approver.'}
      reasonHelperText="A motivation is mandatory for amendments on active contracts."
    >
      {/* Mode is chosen at the action-bar "Interest Amendments" dropdown and
          passed in via initialMode — no in-dialog switch. */}
      {isAdjustment && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            A positive amount <strong>adds to</strong> the next payment (under-accrued); a negative amount{' '}
            <strong>reduces</strong> it (over-accrued). This does not change the engine-calculated interest;
            WHT is applied when the adjustment merges into that payment.
          </Typography>
          {isMultiInvestor && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              On this multi-investor contract the adjustment is apportioned across investors by capital share.
            </Typography>
          )}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid size={4}>
          <TextField
            label={isAdjustment ? 'Effective Date' : 'Payment Date'}
            type="date"
            value={formData.event_date}
            onChange={(e) => handleFieldChange('event_date', e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: contract?.deposit_date?.substring(0, 10),
              max: contract?.maturity_date?.substring(0, 10),
            }}
            helperText={isAdjustment ? 'Consumed by the next payment on/after this date.' : undefined}
          />
        </Grid>
        <Grid size={4}>
          <TextField
            label="Description (Optional)"
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            fullWidth
            placeholder={isAdjustment ? 'e.g. Correct Q2 accrual error' : 'e.g. Quarterly interest payment'}
          />
        </Grid>
        <Grid size={4}>
          <TextField
            label={isAdjustment ? 'Adjustment Amount (+/-)' : 'Interest Amount (Optional)'}
            type="number"
            value={formData.amount}
            onChange={(e) => handleFieldChange('amount', e.target.value)}
            fullWidth
            required={isAdjustment}
            inputProps={isAdjustment ? { step: '0.01' } : { step: '0.01', min: 0 }}
            InputProps={{
              startAdornment: contract?.currency_code
                ? <InputAdornment position="start">{contract.currency_code}</InputAdornment>
                : null,
            }}
            helperText={isAdjustment
              ? 'Signed. Positive = under-accrued (adds); negative = over-accrued (reduces).'
              : 'Leave blank to auto-calculate accrued interest up to the payment date.'}
          />
        </Grid>
      </Grid>
    </ContractActionDialogBase>
    <ScheduleImpactPreviewModal
      open={previewModalOpen}
      onClose={() => setPreviewModalOpen(false)}
      title={isAdjustment ? 'Accrual Adjustment Impact' : 'Ad-Hoc Interest Payment Impact'}
      changeTypeLabel={isAdjustment ? 'Accrual Adjustment' : 'Interest Payment'}
      subtitle={isAdjustment
        ? `Accrual adjustment of ${contract?.currency_code || ''} ${formData.amount} on ${formData.event_date}`
        : `Interest payment${formData.amount !== '' ? ` of ${contract?.currency_code || ''} ${formData.amount}` : ' (auto-calculated)'} on ${formData.event_date}`}
      previewData={previewData}
      contract={contract}
    />
    </>
  );
};

export default AdhocInterestPaymentDialog;
